import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router';
import { Modal } from './Modal';
import { notificationApiService, NotificationItem } from '../services/notificationApiService';
import { debugError } from '../utils/debug';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullhorn } from '@/icons';
import { RichContent } from './RichContent';

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

export const AnnouncementModal: React.FC<Props> = ({ isOpen, onClose }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [announcements, setAnnouncements] = useState<NotificationItem[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);

    const loadAnnouncements = useCallback(async () => {
        try {
            setLoading(true);
            const response = await notificationApiService.listNotifications({ unreadOnly: true, take: 500 });
            if (response.success) {
                const unreadAnnouncements = (response.notifications ?? [])
                    .filter(n => !n.isRead && n.notificationType === 'announcement')
                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                setAnnouncements(unreadAnnouncements);
                setCurrentIndex(0);
            }
        } catch (error) {
            debugError('Error loading announcements:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            loadAnnouncements();
        }
    }, [isOpen, loadAnnouncements]);

    const markCurrentAsRead = async () => {
        const current = announcements[currentIndex];
        if (!current) return;
        try {
            await notificationApiService.toggleRead(current.notificationId, true);
            window.dispatchEvent(new Event('notification-read'));
        } catch (error) {
            debugError('Error marking announcement as read:', error);
        }
    };

    const handleNext = async () => {
        await markCurrentAsRead();
        setCurrentIndex(prev => prev + 1);
    };

    const handleClose = async () => {
        await markCurrentAsRead();
        onClose();
    };

    if (!isOpen) return null;

    const current = announcements[currentIndex];
    const isLast = currentIndex >= announcements.length - 1;
    const total = announcements.length;
    const displayIndex = currentIndex + 1;

    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        } catch {
            return dateString;
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Announcement">
            <div style={{ minWidth: '400px' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.text.secondary }}>
                        Loading...
                    </div>
                ) : !current ? (
                    <div style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.text.muted }}>
                        No new announcements.
                    </div>
                ) : (
                    <>
                        {/* Counter */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: theme.spacing.md,
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing.sm,
                                color: theme.colors.primary,
                                fontSize: theme.typography.fontSize.sm,
                                fontWeight: theme.typography.fontWeight.semibold,
                            }}>
                                <FontAwesomeIcon icon={faBullhorn} />
                                {displayIndex} of {total} announcement{total !== 1 ? 's' : ''}
                            </div>
                            <div style={{
                                fontSize: theme.typography.fontSize.xs,
                                color: theme.colors.text.muted,
                            }}>
                                {formatDate(current.createdAt)}
                            </div>
                        </div>

                        {/* Title */}
                        <h3 style={{
                            fontSize: theme.typography.fontSize.lg,
                            fontWeight: theme.typography.fontWeight.bold,
                            color: theme.colors.text.primary,
                            margin: `0 0 ${theme.spacing.md} 0`,
                        }}>
                            {current.title}
                        </h3>

                        {/* Message */}
                        <div style={{
                            fontSize: theme.typography.fontSize.sm,
                            color: theme.colors.text.secondary,
                            lineHeight: '1.6',
                            whiteSpace: current.messageFormat === 'html' ? 'normal' : 'pre-wrap',
                            padding: theme.spacing.md,
                            backgroundColor: theme.colors.surfaceSecondary,
                            borderRadius: theme.borderRadius.md,
                            border: `1px solid ${theme.colors.border.default}`,
                            maxHeight: '300px',
                            overflowY: 'auto',
                        }}>
                            <RichContent message={current.message ?? ''} messageFormat={current.messageFormat} notificationId={current.notificationId} navigate={navigate} />
                        </div>

                        {/* Footer */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: theme.spacing.lg,
                            paddingTop: theme.spacing.md,
                            borderTop: `1px solid ${theme.colors.border.default}`,
                        }}>
                            <div>
                                {currentIndex > 0 && (
                                    <button
                                        onClick={() => setCurrentIndex(prev => prev - 1)}
                                        style={{
                                            padding: `${theme.spacing.sm} ${theme.spacing.xl}`,
                                            backgroundColor: 'transparent',
                                            color: theme.colors.text.secondary,
                                            border: `1px solid ${theme.colors.border.default}`,
                                            borderRadius: theme.borderRadius.sm,
                                            fontSize: theme.typography.fontSize.sm,
                                            fontWeight: theme.typography.fontWeight.medium,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Previous
                                    </button>
                                )}
                            </div>
                            <button
                                onClick={isLast ? handleClose : handleNext}
                                style={{
                                    padding: `${theme.spacing.sm} ${theme.spacing.xl}`,
                                    backgroundColor: theme.colors.primary,
                                    color: theme.colors.text.inverse,
                                    border: 'none',
                                    borderRadius: theme.borderRadius.sm,
                                    fontSize: theme.typography.fontSize.sm,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    cursor: 'pointer',
                                }}
                            >
                                {isLast ? 'Close' : 'Next'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};
